import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { IconButton } from '@/components/ui/IconButton'
import { Tag } from '@/components/ui/Tag'
import {
  useAmendRole,
  useCapabilityCatalogue,
  useCreateRole,
  useDeleteRole,
  useRoles,
} from '@/features/settings/api/useRoles'
import { RoleFormSheet } from '@/features/settings/components/RoleFormSheet'
import { roleRefusalMessage } from '@/features/settings/roleRefusals'
import { roleDescription, roleLabel } from '@/lib/navigation'
import type { RoleChange, RoleRecord } from '@/types/domain'

/* Roles & permissions — the editable view.
 *
 * This replaces the read-only reference table that stood here while the five
 * roles were fixed (AD-7). Migration 0021 made roles rows, so the question this
 * screen answers changed from "what can Pricing see?" to "what should this role
 * be able to do?" — and the answer is now something a founder writes rather
 * than something the PRD did.
 *
 * The table has not gone away; it moved below (`AccessMatrixPanel`) and is now
 * built from these same records, a column per role — so "who may do X?",
 * which one role at a time cannot answer, is answered for every role, the ones
 * a founder created included (N-09).
 *
 * Two refusals are rendered as ABSENT actions rather than disabled ones:
 * the superuser role has no delete
 * button, and a system role has no delete button. A role somebody still holds
 * DOES show one, because that refusal is about state a founder can change — the
 * server answers `role-in-use`, and the Banner above the list tells them to
 * reassign the holders first.
 */

export function RolesPanel() {
  const { t } = useTranslation()
  const roles = useRoles()
  const catalogue = useCapabilityCatalogue()

  const createRole = useCreateRole()
  const amendRole = useAmendRole()
  const deleteRole = useDeleteRole()

  const [editing, setEditing] = useState<RoleRecord | undefined>()
  const [sheetOpen, setSheetOpen] = useState(false)

  function openCreate() {
    setEditing(undefined)
    setSheetOpen(true)
  }

  function openEdit(role: RoleRecord) {
    setEditing(role)
    setSheetOpen(true)
  }

  function submit(change: RoleChange) {
    const mutation = editing
      ? amendRole.mutateAsync({ key: editing.key, change })
      : createRole.mutateAsync(change)

    void mutation.then(
      () => {
        setSheetOpen(false)
        setEditing(undefined)
      },
      // A refusal is the mutation's `.error`, worded in the sheet below; caught
      // here so it is not ALSO an unhandled rejection.
      () => undefined,
    )
  }

  const pending = createRole.isPending || amendRole.isPending
  // The sheet shows whichever mutation is actually in flight. Both are never
  // pending at once — the sheet does one or the other. It takes the RAW
  // refusal and words it itself (`roleRefusals.ts`): a `key` / `name` /
  // `description` code under its field, everything else in its banner. Never
  // `.message`, which is the problem's English `detail` or `title` (S-13).
  const failure = editing ? amendRole.error : createRole.error

  return (
    <Card>
      <CardHeader
        action={
          <Button onClick={openCreate} iconLeft={Plus} size="sm">
            {t('settings.roles.create')}
          </Button>
        }
      >
        <CardTitle>{t('settings.roles.title')}</CardTitle>
      </CardHeader>

      <CardBody className="space-y-3">
        <p className="text-sm text-text-muted">{t('settings.roles.intro')}</p>

        {roles.isPending && <p className="text-sm text-text-muted">{t('settings.roles.loading')}</p>}

        {roles.isError && (
          <p className="text-sm text-negative">{t('settings.roles.loadFailed')}</p>
        )}

        {/* A refused delete is said ABOVE the list, where it is seen without
            scrolling, and names the reason — a role somebody still holds, or
            (from a stale tab) a built-in or superuser role, or the last
            super-admin. Dismissing it clears the refusal; it sends nothing, so
            it is «Скрыть», not «Повторить» (S-13). */}
        {deleteRole.error && (
          <div role="alert">
            <Banner
              tone="attention"
              title={roleRefusalMessage(deleteRole.error, t)}
              action={
                <Button size="sm" variant="secondary" onClick={() => deleteRole.reset()}>
                  {t('common.actions.dismiss')}
                </Button>
              }
            />
          </div>
        )}

        {roles.data?.map((role) => {
          /* A seeded role is NAMED by its key, like every other place a role
             is named, so "Sales" reads «Продажи» here too. Its DESCRIPTION is an
             admin's free text and is translated only while it is still the seed
             (`roleDescription`). Both render-time only: the editor below is
             handed the stored record and writes back what it was given. */
          const label = roleLabel(role.key, role.name)
          const name = label.translated ? t(label.key) : label.text
          const about = roleDescription(role.key, role.description)
          return (
            <div
              key={role.key}
              className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-heading">{name}</span>
                  {role.isSuperuser && (
                    <Tag tone="brand">{t('settings.roles.badges.superuser')}</Tag>
                  )}
                  {role.isSystem && !role.isSuperuser && (
                    <Tag>{t('settings.roles.badges.system')}</Tag>
                  )}
                </div>

                <p className="truncate text-sm text-text-muted">
                  {about.translated ? t(about.key) : about.text}
                </p>

                <p className="mt-1 text-xs text-text-muted">
                  {/* The superuser's stored capability list is empty — the flag is
                      what grants access — so printing a count would say "0
                      permissions" about the role that holds every one. */}
                  {role.isSuperuser
                    ? t('settings.roles.summary.allPermissions')
                    : t('settings.roles.summary.counts', {
                        capabilities: role.capabilities.length,
                        surfaces: Object.keys(role.scopes).length,
                      })}
                  {' · '}
                  {t('settings.roles.summary.users', { count: role.userCount })}
                </p>
              </div>

              <div className="flex shrink-0 gap-1">
                <IconButton
                  icon={Pencil}
                  label={t('settings.roles.editAction', { name })}
                  onClick={() => openEdit(role)}
                />
                {/* Absent, not disabled, for the two roles the server will never
                    delete — a greyed-out button invites a click that can only
                    fail.

                    A `danger` TEXT button rather than an icon: deleting a role is
                    destructive, and destructive actions always carry a word. */}
                {!role.isSystem && !role.isSuperuser && (
                  <Button
                    variant="danger"
                    size="sm"
                    iconLeft={Trash2}
                    onClick={() => void deleteRole.mutate(role.key)}
                  >
                    {t('common.actions.delete')}
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </CardBody>

      {/* The editor needs the server's vocabulary before it can render a single
          checkbox, so it is mounted only once the catalogue has arrived. */}
      {sheetOpen && catalogue.data && (
        <RoleFormSheet
          open={sheetOpen}
          onClose={() => {
            setSheetOpen(false)
            setEditing(undefined)
          }}
          role={editing}
          catalogue={catalogue.data}
          onSubmit={submit}
          pending={pending}
          failure={failure}
        />
      )}
    </Card>
  )
}
